// LOGOMAN PRO — pure-JS color palette extractor.
// No external deps. Quantizes pixels into buckets, returns the most common.

export interface ExtractedColor {
  hex: string;
  r: number;
  g: number;
  b: number;
  count: number;
  /** Perceptual lightness 0..1 (Y from sRGB). */
  lightness: number;
  /** Saturation proxy (max-min)/max, 0..1. */
  saturation: number;
}

export interface ExtractOptions {
  /** How many colors to return. Default 8. */
  count?: number;
  /** Quantization bucket size (4–32). Smaller = more granular. Default 16. */
  bucket?: number;
  /** Drop near-white & near-black so they don't dominate. Default true. */
  ignoreExtremes?: boolean;
  /** Drop very desaturated (gray) pixels. Default true. */
  preferColorful?: boolean;
}

/** Extract a color palette from an image file. */
export async function extractPaletteFromFile(
  file: File,
  opts: ExtractOptions = {}
): Promise<ExtractedColor[]> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return extractPaletteFromImage(img, opts);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Extract a color palette from a loaded HTMLImageElement. */
export function extractPaletteFromImage(
  img: HTMLImageElement,
  opts: ExtractOptions = {}
): ExtractedColor[] {
  const count = opts.count ?? 8;
  const bucket = clamp(opts.bucket ?? 16, 4, 64);
  const ignoreExtremes = opts.ignoreExtremes ?? true;
  const preferColorful = opts.preferColorful ?? true;

  // Downscale to ~120px on the long side for speed.
  const maxDim = 120;
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  // Bucket pixels into a quantized 3-tuple key.
  const buckets = new Map<string, { r: number; g: number; b: number; count: number }>();
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 128) continue; // skip transparent
    const r = data[i], g = data[i + 1], b = data[i + 2];

    if (ignoreExtremes) {
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      if (max < 18) continue;       // near black
      if (min > 240) continue;      // near white
    }
    if (preferColorful) {
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const sat = max === 0 ? 0 : (max - min) / max;
      if (sat < 0.08 && (r + g + b) / 3 > 200) continue; // light gray
    }

    const qr = Math.round(r / bucket) * bucket;
    const qg = Math.round(g / bucket) * bucket;
    const qb = Math.round(b / bucket) * bucket;
    const key = `${qr},${qg},${qb}`;
    const entry = buckets.get(key);
    if (entry) {
      entry.r += r; entry.g += g; entry.b += b; entry.count++;
    } else {
      buckets.set(key, { r, g, b, count: 1 });
    }
  }

  // Average each bucket and sort by frequency.
  const palette: ExtractedColor[] = [];
  for (const v of buckets.values()) {
    const r = Math.round(v.r / v.count);
    const g = Math.round(v.g / v.count);
    const b = Math.round(v.b / v.count);
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    palette.push({
      hex: rgbToHex(r, g, b),
      r, g, b,
      count: v.count,
      lightness: (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255,
      saturation: max === 0 ? 0 : (max - min) / max,
    });
  }
  palette.sort((a, b) => b.count - a.count);

  // Deduplicate visually-similar colors.
  const deduped: ExtractedColor[] = [];
  const minDist = 28;
  for (const c of palette) {
    if (deduped.every((d) => colorDist(c, d) >= minDist)) deduped.push(c);
    if (deduped.length >= count) break;
  }
  return deduped;
}

export interface ColorAssignment {
  background: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
}

/** Generate N visually-distinct logo color combos from a palette. */
export function generateVariations(palette: ExtractedColor[], n = 8): ColorAssignment[] {
  if (palette.length === 0) return [];

  const sortedByLight = [...palette].sort((a, b) => b.lightness - a.lightness);
  const lightest = sortedByLight[0];
  const darkest = sortedByLight[sortedByLight.length - 1];
  const sortedBySat = [...palette].sort((a, b) => b.saturation - a.saturation);

  const variations: ColorAssignment[] = [];
  const accents = sortedBySat.slice(0, Math.min(palette.length, 6));

  // Variation 1: lightest bg, darkest title, most-saturated icon
  variations.push({
    background: lightest.hex,
    titleColor: darkest.hex,
    sloganColor: midTone(palette).hex,
    iconColor: accents[0]?.hex || darkest.hex,
  });

  // Variation 2: dark bg, lightest title, accent icon
  variations.push({
    background: darkest.hex,
    titleColor: lightest.hex,
    sloganColor: lightenOrDarken(lightest.hex, -10),
    iconColor: accents[0]?.hex || lightest.hex,
  });

  // Variations 3-N: cycle accents as the icon color, dark text on light bg
  for (let i = 1; i < accents.length && variations.length < n; i++) {
    variations.push({
      background: lightest.hex,
      titleColor: darkest.hex,
      sloganColor: midTone(palette).hex,
      iconColor: accents[i].hex,
    });
  }

  // Variation: monochromatic — different shades of the most saturated color
  if (variations.length < n && accents[0]) {
    const base = accents[0].hex;
    variations.push({
      background: lightenOrDarken(base, 75),
      titleColor: lightenOrDarken(base, -40),
      sloganColor: lightenOrDarken(base, -20),
      iconColor: base,
    });
  }

  // Variation: white bg, palette[0] title, palette[1] icon
  if (variations.length < n) {
    variations.push({
      background: "#ffffff",
      titleColor: darkest.hex,
      sloganColor: midTone(palette).hex,
      iconColor: accents[0]?.hex || darkest.hex,
    });
  }

  return variations.slice(0, n);
}

// ---------- helpers ----------
function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }
function rgbToHex(r: number, g: number, b: number) {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function colorDist(a: ExtractedColor, b: ExtractedColor) {
  const dr = a.r - b.r, dg = a.g - b.g, db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}
function midTone(palette: ExtractedColor[]): ExtractedColor {
  const sorted = [...palette].sort((a, b) => a.lightness - b.lightness);
  return sorted[Math.floor(sorted.length / 2)];
}
function lightenOrDarken(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  r = clamp(r + amount, 0, 255); g = clamp(g + amount, 0, 255); b = clamp(b + amount, 0, 255);
  return rgbToHex(r, g, b);
}
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
